import { FC, useState, useEffect, useMemo } from 'react';

interface DarkModeIconProps {
    src: string;
    alt: string;
    className?: string;
    width?: string;
    height?: string;
}

/**
 * Wrapper component that applies dark mode styling to icons
 * Uses SVG filter to convert colored icons to gray-500 in light mode and zinc-300 in dark mode
 */
export const DarkModeIcon: FC<DarkModeIconProps> = ({ src, className, width = '16px', height = '16px' }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        const updateDarkMode = () => {
            setIsDarkMode(document.body.getAttribute('data-theme') === 'dark');
        };
        
        updateDarkMode();
        
        const observer = new MutationObserver(updateDarkMode);
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        
        return () => observer.disconnect();
    }, []);

    const filterId = useMemo(() => `darkModeIconFilter-${Math.random()}`, []);

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${parseInt(width)} ${parseInt(height)}`}
            style={{ overflow: 'visible' }}
        >
            <defs>
                {isDarkMode ? (
                    <filter id={filterId}>
                        <feColorMatrix type="saturate" values="0" />
                        <feComponentTransfer>
                            <feFuncR type="linear" slope="2.0" />
                            <feFuncG type="linear" slope="2.0" />
                            <feFuncB type="linear" slope="2.0" />
                        </feComponentTransfer>
                    </filter>
                ) : (
                    <filter id={filterId}>
                        <feColorMatrix type="saturate" values="0" />
                        <feComponentTransfer>
                            <feFuncR type="linear" slope="0.15" intercept="0.1" />
                            <feFuncG type="linear" slope="0.15" intercept="0.1" />
                            <feFuncB type="linear" slope="0.15" intercept="0.1" />
                        </feComponentTransfer>
                    </filter>
                )}
            </defs>
            <image
                href={src}
                width={parseInt(width)}
                height={parseInt(height)}
                className={className}
                style={{
                    filter: `url(#${filterId})`,
                    objectFit: 'contain'
                }}
            />
        </svg>
    );
};
